/*
 * Nibby cart helpers (vanilla JS, no dependencies).
 * - [data-nibby-subscribe]: switches a one-time cart line to a subscription (selling plan).
 * - [data-nibby-add]: adds a cross-sell variant.
 * After each change the cart drawer and cart icon are re-rendered with the Section Rendering API,
 * using the same containers Dawn's CartDrawerItems uses (#CartDrawer .drawer__inner, #cart-icon-bubble).
 * On the cart page (/cart) the page is reloaded instead.
 * - Subscription gifts: the $0 variants in window.nibbyGiftIds (theme setting) are added while the cart has a
 *   subscription line and removed when it has none (line property _nibby_gift), so they appear in checkout.
 */
(function () {
  if (window.nibbyCartInit) return;
  window.nibbyCartInit = true;

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  function isCartPage() {
    return document.getElementById('main-cart-items') !== null;
  }

  function renderSections(sections) {
    if (!sections) return;
    const parser = new DOMParser();

    if (sections['cart-drawer']) {
      const target = document.querySelector('#CartDrawer .drawer__inner');
      const source = parser.parseFromString(sections['cart-drawer'], 'text/html').querySelector('.drawer__inner');
      if (target && source) target.innerHTML = source.innerHTML;
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.remove('is-empty');
    }

    if (sections['cart-icon-bubble']) {
      const target = document.getElementById('cart-icon-bubble');
      const source = parser
        .parseFromString(sections['cart-icon-bubble'], 'text/html')
        .querySelector('.shopify-section');
      if (target && source) target.innerHTML = source.innerHTML;
    }
  }

  async function post(url, body, button) {
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(
          Object.assign(body, { sections: SECTIONS.join(','), sections_url: window.location.pathname })
        ),
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || state.message || 'Cart update failed');

      if (isCartPage()) {
        window.location.reload();
        return;
      }
      renderSections(state.sections);
      const inner = document.querySelector('#CartDrawer .drawer__inner');
      if (inner) {
        if (typeof trapFocus === 'function') trapFocus(inner);
        else inner.focus();
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nibby-cart', cartData: state });
      }
    } catch (error) {
      console.error(error);
      button.removeAttribute('aria-busy');
      button.disabled = false;
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = error.message;
    }
  }

  /* ---------- Subscription gifts as real $0 cart lines ---------- */
  let syncing = false;
  async function syncGifts() {
    const ids = String(window.nibbyGiftIds || '')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter(Boolean);
    if (!ids.length || syncing) return;
    syncing = true;
    try {
      const cart = await (await fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })).json();
      const isGift = (item) => item.properties && item.properties._nibby_gift;
      const hasSubscription = cart.items.some((item) => item.selling_plan_allocation && !isGift(item));
      const gifts = cart.items.filter(isGift);
      const updates = {};
      let toAdd = [];

      if (hasSubscription) {
        toAdd = ids
          .filter((id) => !gifts.some((item) => item.variant_id === id))
          .map((id) => ({ id: id, quantity: 1, properties: { _nibby_gift: '1' } }));
        gifts.forEach((item) => {
          if (!ids.includes(item.variant_id)) updates[item.key] = 0;
          else if (item.quantity !== 1) updates[item.key] = 1;
        });
      } else {
        gifts.forEach((item) => {
          updates[item.key] = 0;
        });
      }

      const hasUpdates = Object.keys(updates).length > 0;
      if (!toAdd.length && !hasUpdates) return;

      if (hasUpdates) {
        await fetch(window.routes.cart_update_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates }),
        });
      }
      if (toAdd.length) {
        await fetch(window.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: toAdd }),
        });
      }

      if (isCartPage()) {
        window.location.reload();
        return;
      }
      const sections = await (
        await fetch(window.location.pathname + '?sections=' + SECTIONS.join(','), { headers: { Accept: 'application/json' } })
      ).json();
      renderSections(sections);
    } catch (error) {
      console.error(error);
    } finally {
      syncing = false;
    }
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'nibby-gifts') window.setTimeout(syncGifts, 50);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncGifts);
  else syncGifts();

  document.addEventListener('click', (event) => {
    const subscribe = event.target.closest('[data-nibby-subscribe]');
    if (subscribe) {
      event.preventDefault();
      post(
        window.routes.cart_change_url + '.js',
        {
          id: subscribe.dataset.lineKey,
          quantity: Number(subscribe.dataset.quantity),
          selling_plan: Number(subscribe.dataset.sellingPlan),
        },
        subscribe
      );
      return;
    }

    const add = event.target.closest('[data-nibby-add]');
    if (add) {
      event.preventDefault();
      post(window.routes.cart_add_url + '.js', { items: [{ id: Number(add.dataset.variantId), quantity: 1 }] }, add);
    }
  });
})();
